import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Home, ArrowLeft, ArrowRight, Check, MapPin, DollarSign, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { sanitizeDisplayText, PRICE_TRUST_LINE } from "../utils/priceUtils";
import { EstimateCard } from "../components/common/EstimateCard";
import { CustomServiceRequest } from "../components/wizard/CustomServiceRequest";
import { OutOfAreaWaitlist } from "../components/waitlist/OutOfAreaWaitlist";

// Import modular Plumbing flow
import { PlumbingFlow, calculatePlumbingEstimate } from "../features/plumbing";

// Import modular Handyman flow (permanently enabled)
import { HandymanFlow, calculateHandymanEstimate } from "../features/handyman";

// Import modular Painting flow
import { PaintingFlow } from "../features/painting";
import { ElectricalFlow } from "../features/electrical";

// Photo upload step (shared across all categories)
import PhotoUpload from "../components/wizard/PhotoUpload";
import { usePhotoUpload } from "../hooks/usePhotoUpload";

// Square footage options in 500 sqft increments
const SQFT_OPTIONS = [
  { value: "under_1000", label: "Under 1,000 sq ft", sqft: 750 },
  { value: "1000_1500", label: "1,000 - 1,500 sq ft", sqft: 1250 },
  { value: "1500_2000", label: "1,500 - 2,000 sq ft", sqft: 1750 },
  { value: "2000_2500", label: "2,000 - 2,500 sq ft", sqft: 2250 },
  { value: "2500_3000", label: "2,500 - 3,000 sq ft", sqft: 2750 },
  { value: "3000_3500", label: "3,000 - 3,500 sq ft", sqft: 3250 },
  { value: "3500_4000", label: "3,500 - 4,000 sq ft", sqft: 3750 },
  { value: "4000_4500", label: "4,000 - 4,500 sq ft", sqft: 4250 },
  { value: "4500_5000", label: "4,500 - 5,000 sq ft", sqft: 4750 },
  { value: "5000_plus", label: "5,000+ sq ft", sqft: 5500 }
];

// Price ranges based on specific answers (not just category)
const ANSWER_PRICE_RANGES = {
  // Painting type selections - base price before item selection
  painting_type: {
    interior: { low: 0, high: 0 }, // Will be calculated from room_types
    exterior: { low: 1500, high: 8000 },
    specialty: { low: 800, high: 5000 }
  },
  // Interior room counts - no longer used for pricing, just for info
  interior_rooms: {
    "1-2": { low: 0, high: 0 },
    "3-4": { low: 0, high: 0 },
    "5+": { low: 0, high: 0 }
  },
  // Exterior type
  exterior_type: {
    partial: { low: 500, high: 2000 },
    full_house: { low: 2000, high: 8000 },
    standalone: { low: 150, high: 500 },
    specialty: { low: 1000, high: 5000 }
  },
  // Project size (generic)
  project_size: {
    small: { low: 150, high: 500 },
    medium: { low: 400, high: 1500 },
    large: { low: 1000, high: 5000 }
  },
  // Ceiling height modifiers
  interior_ceiling_height: {
    standard: { low: 0, high: 0 },
    high: { low: 100, high: 300 },
    very_high: { low: 200, high: 600 }
  },
  // Stories modifier
  stories: {
    "1": { low: 0, high: 0 },
    "2+": { low: 200, high: 800 }
  }
};

// ==================== ELECTRICAL: migrated to /features/electrical ====================
// The Electrical constants and questions previously defined here have been
// moved into the modular /features/electrical package. They are now imported
// from that package by ElectricalFlow.jsx.


// Category base prices (used as fallback for generic categories)
const CATEGORY_BASE_PRICES = {
  painting: { low: 0, high: 0 }, // Calculated from answers
  flooring: { low: 0, high: 0 }, // Calculated from sqft
  countertops: { low: 0, high: 0 }, // Calculated from sqft
  landscaping: { low: 200, high: 5000 },
  "remodel-restoration": { low: 500, high: 25000 }
};

// Pricing constants for new categories
const FLOORING_PRICE_PER_SQFT = { low: 2.50, high: 5.50 };
const COUNTERTOPS_PRICE_PER_SQFT = { low: 2.50, high: 5.50 };

// Flooring type-specific labor pricing per sq ft (labor-only)
const FLOORING_TYPE_PRICES = {
  laminate: { low: 2.00, high: 3.50 },
  luxury_vinyl: { low: 2.50, high: 4.50 },
  vinyl_linoleum: { low: 1.50, high: 3.00 },
  wood: { low: 4.00, high: 7.00 },
  carpet: { low: 1.50, high: 3.00 },
  ceramic_tile: { low: 4.50, high: 8.00 },
  natural_stone: { low: 6.00, high: 12.00 }
};

// Flooring removal labor add-on per sq ft
const FLOORING_REMOVAL_PRICE = { low: 1.50, high: 3.00 };

// Room square footage percentages for flooring estimate
const FLOORING_ROOM_PERCENTAGES = {
  entire_house: 1.0,
  all_bedrooms: 0.30,
  bedroom_1: 0.12,
  bedroom_2: 0.10,
  more_bedrooms: 0.08,
  living_room: 0.20,
  dining_room: 0.10,
  kitchen: 0.12,
  hallways: 0.08,
  bathrooms: 0.10, // Will be adjusted by specific bathroom selection
  master_bathroom: 0.05,
  guest_bathroom: 0.03,
  additional_bathrooms: 0.02
};

// Flooring labor-only disclaimer
const FLOORING_DISCLAIMER = "Labor-only estimate. Materials, floor leveling, trim work, and disposal are not included unless specified.";

// Question definitions for Flooring
const FLOORING_QUESTIONS = [
  {
    id: "home_sqft",
    question: "What is the square footage of your home?",
    type: "number",
    placeholder: "Enter total home square footage (e.g., 1800)",
    unit: "sq ft",
    required: true
  },
  {
    id: "flooring_type",
    question: "What type of flooring are you interested in?",
    type: "radio",
    options: [
      { value: "laminate", label: "Laminate" },
      { value: "luxury_vinyl", label: "Luxury vinyl (plank or tile)" },
      { value: "vinyl_linoleum", label: "Traditional vinyl / linoleum (tile or sheet)" },
      { value: "wood", label: "Wood" },
      { value: "carpet", label: "Carpet" },
      { value: "ceramic_tile", label: "Ceramic or porcelain tile" },
      { value: "natural_stone", label: "Natural stone tile" }
    ]
  },
  {
    id: "flooring_rooms",
    question: "Which rooms need new flooring?",
    type: "checkbox",
    options: [
      { value: "entire_house", label: "Entire house" },
      { value: "all_bedrooms", label: "All bedrooms" },
      { value: "bedroom_1", label: "Bedroom 1" },
      { value: "bedroom_2", label: "Bedroom 2" },
      { value: "more_bedrooms", label: "More bedrooms" },
      { value: "living_room", label: "Living room" },
      { value: "dining_room", label: "Dining room" },
      { value: "kitchen", label: "Kitchen" },
      { value: "hallways", label: "Hallways" },
      { value: "bathrooms", label: "Bathrooms" }
    ]
  },
  {
    id: "flooring_bathrooms",
    question: "Which bathrooms need flooring?",
    type: "checkbox",
    showIfIncludes: { flooring_rooms: "bathrooms" },
    options: [
      { value: "master_bathroom", label: "Master Bathroom" },
      { value: "guest_bathroom", label: "Guest Bathroom" },
      { value: "additional_bathrooms", label: "Additional Bathroom(s)" }
    ]
  },
  {
    id: "flooring_removal",
    question: "Remove existing flooring?",
    type: "radio",
    options: [
      { value: "no", label: "No, just new installation" },
      { value: "yes", label: "Yes, remove existing flooring (+$1.50–$3.00 per sq ft labor)" }
    ]
  },
  {
    id: "timeline",
    question: "When do you need this done?",
    type: "radio",
    options: [
      { value: "1_week", label: "Within a week" },
      { value: "2_weeks", label: "Within 2 weeks" },
      { value: "1_month", label: "Within a month" },
      { value: "quote_only", label: "Just Getting a Quote" }
    ]
  }
];

// Question definitions for Countertops
const COUNTERTOPS_QUESTIONS = [
  {
    id: "countertops_sqft",
    question: "What is the square footage of countertop area?",
    type: "number",
    placeholder: "Enter square footage (e.g., 40)",
    unit: "sq ft"
  },
  {
    id: "countertops_material",
    question: "What material are you interested in?",
    type: "radio",
    options: [
      { value: "granite", label: "Granite" },
      { value: "quartz", label: "Quartz" },
      { value: "marble", label: "Marble" },
      { value: "butcher_block", label: "Butcher Block" },
      { value: "laminate", label: "Laminate" },
      { value: "other", label: "Other / Not sure" }
    ]
  },
  {
    id: "countertops_location",
    question: "Where will the countertops be installed?",
    type: "checkbox",
    options: [
      { value: "kitchen", label: "Kitchen" },
      { value: "bathroom", label: "Bathroom" },
      { value: "laundry", label: "Laundry room" },
      { value: "other", label: "Other area" }
    ]
  },
  {
    id: "timeline",
    question: "When do you need this done?",
    type: "radio",
    options: [
      { value: "1_week", label: "Within a week" },
      { value: "2_weeks", label: "Within 2 weeks" },
      { value: "1_month", label: "Within a month" },
      { value: "quote_only", label: "Just Getting a Quote" }
    ]
  }
];

// Question definitions for Landscaping
const LANDSCAPING_QUESTIONS = [
  {
    id: "lot_size",
    question: "How big is your lot?",
    type: "number_with_unit",
    placeholder: "Enter size",
    units: [
      { value: "sqft", label: "Square Feet" },
      { value: "acres", label: "Acres" }
    ]
  },
  {
    id: "house_sqft",
    question: "What is the square footage of your house?",
    type: "number",
    placeholder: "Enter square footage (e.g., 2000)",
    unit: "sq ft"
  },
  {
    id: "landscaping_type",
    question: "What type of landscaping work do you need?",
    type: "checkbox",
    options: [
      { value: "lawn_care", label: "Lawn care / mowing" },
      { value: "planting", label: "Planting / garden beds" },
      { value: "irrigation", label: "Irrigation / sprinklers" },
      { value: "hardscape", label: "Hardscape (patios, walkways)" },
      { value: "tree_service", label: "Tree trimming / removal" },
      { value: "full_design", label: "Full landscape design" },
      { value: "other", label: "Other" }
    ]
  },
  {
    id: "timeline",
    question: "When do you need this done?",
    type: "radio",
    options: [
      { value: "1_week", label: "Within a week" },
      { value: "2_weeks", label: "Within 2 weeks" },
      { value: "1_month", label: "Within a month" },
      { value: "quote_only", label: "Just Getting a Quote" }
    ]
  }
];

// Question definitions for Remodel / Restoration
const REMODEL_RESTORATION_QUESTIONS = [
  {
    id: "remodel_type",
    question: "What type of project do you need?",
    type: "checkbox",
    options: [
      { value: "kitchen", label: "Kitchen remodel" },
      { value: "bathroom", label: "Bathroom remodel" },
      { value: "basement", label: "Basement finishing" },
      { value: "addition", label: "Room addition" },
      { value: "water_damage", label: "Water damage restoration" },
      { value: "fire_damage", label: "Fire damage restoration" },
      { value: "mold", label: "Mold remediation" },
      { value: "other", label: "Other renovation" }
    ]
  },
  {
    id: "remodel_scope",
    question: "How would you describe the scope?",
    type: "radio",
    options: [
      { value: "refresh", label: "Refresh / cosmetic updates" },
      { value: "partial", label: "Partial remodel" },
      { value: "full", label: "Full gut remodel" },
      { value: "restoration", label: "Damage restoration" }
    ]
  },
  {
    id: "project_description",
    question: "Please describe your project:",
    type: "text",
    placeholder: "Tell us more about what you need..."
  },
  {
    id: "timeline",
    question: "When do you need this done?",
    type: "radio",
    options: [
      { value: "emergency", label: "Emergency - ASAP" },
      { value: "1_month", label: "Within a month" },
      { value: "3_months", label: "Within 3 months" },
      { value: "quote_only", label: "Just Getting a Quote" }
    ]
  }
];

// Question definitions for each category
const GENERIC_QUESTIONS = [
  {
    id: "project_size",
    question: "How would you describe the size of your project?",
    type: "radio",
    options: [
      { value: "small", label: "Small - Minor repair or single item" },
      { value: "medium", label: "Medium - Multiple items or one room" },
      { value: "large", label: "Large - Multiple rooms or major work" }
    ]
  },
  {
    id: "timeline",
    question: "When do you need this done?",
    type: "radio",
    options: [
      { value: "emergency", label: "Emergency - ASAP" },
      { value: "1-2_weeks", label: "Within 1-2 weeks" },
      { value: "1_month", label: "Within a month" },
      { value: "quote_only", label: "Just Getting a Quote" }
    ]
  }
];

// ==================== ELECTRICAL QUESTIONS: migrated to /features/electrical ====================


const CATEGORY_QUESTIONS = {
  flooring: FLOORING_QUESTIONS,
  countertops: COUNTERTOPS_QUESTIONS,
  landscaping: LANDSCAPING_QUESTIONS,
  "remodel-restoration": REMODEL_RESTORATION_QUESTIONS
};

export default function RequestWizard() {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [step, setStep] = useState(0); // 0 = categories, 1 = zip, 2+ = questions
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null); // Single selection only
  const [zipCode, setZipCode] = useState("");
  const [homeSqft, setHomeSqft] = useState(""); // Square footage for painting
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [additionalDetails, setAdditionalDetails] = useState("");
  const [unlistedServiceDescription, setUnlistedServiceDescription] = useState("");
  
  // Photo upload step state (photos persist across wizard navigation)
  const [photoStepData, setPhotoStepData] = useState(null);
  const photoUpload = usePhotoUpload();
  
  // Max characters for additional details
  const MAX_ADDITIONAL_DETAILS = 1000;
  
  // Track expanded categories for handyman collapsible UI
  const [expandedCategories, setExpandedCategories] = useState([]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      /* silent - error deliberately ignored */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (categorySlug && categories.length > 0) {
      const cat = categories.find(c => c.slug === categorySlug);
      if (cat) {
        setSelectedCategory(cat);
        setStep(1); // Skip to zip code entry
      }
    }
  }, [categorySlug, categories]);

  // Calculate estimated price range based on user answers (not just categories)
  const estimatedPriceRange = useMemo(() => {
    // Only show estimate once user has made specific selections in the wizard
    const answerKeys = Object.keys(answers);
    if (answerKeys.length === 0) return null;
    
    let totalLow = 0;
    let totalHigh = 0;
    let hasValidPrice = false;
    
    // Check for generic project_size answers (used by other categories)
    if (answers.project_size && ANSWER_PRICE_RANGES.project_size[answers.project_size]) {
      const range = ANSWER_PRICE_RANGES.project_size[answers.project_size];
      totalLow += range.low;
      totalHigh += range.high;
      hasValidPrice = true;
    }
    
    // ==================== FLOORING: Room-Based Pricing ====================
    if (answers.flooring_rooms && Array.isArray(answers.flooring_rooms) && answers.flooring_rooms.length > 0) {
      const homeSqftValue = parseFloat(answers.home_sqft) || 0;
      
      if (homeSqftValue > 0) {
        // Calculate total square footage from selected rooms
        let flooringSqft = 0;
        const selectedRooms = answers.flooring_rooms;
        
        // If entire house selected, use full sqft
        if (selectedRooms.includes('entire_house')) {
          flooringSqft = homeSqftValue;
        } else {
          // Add up percentages for selected rooms
          selectedRooms.forEach(room => {
            if (room !== 'bathrooms' && FLOORING_ROOM_PERCENTAGES[room]) {
              flooringSqft += homeSqftValue * FLOORING_ROOM_PERCENTAGES[room];
            }
          });
          
          // Handle bathroom sub-selections if bathrooms is selected
          if (selectedRooms.includes('bathrooms') && answers.flooring_bathrooms && Array.isArray(answers.flooring_bathrooms)) {
            answers.flooring_bathrooms.forEach(bathroom => {
              if (FLOORING_ROOM_PERCENTAGES[bathroom]) {
                flooringSqft += homeSqftValue * FLOORING_ROOM_PERCENTAGES[bathroom];
              }
            });
          } else if (selectedRooms.includes('bathrooms')) {
            // Default bathroom percentage if no specific bathrooms selected
            flooringSqft += homeSqftValue * FLOORING_ROOM_PERCENTAGES.bathrooms;
          }
        }
        
        if (flooringSqft > 0) {
          // Get type-specific pricing or use default
          const flooringType = answers.flooring_type;
          const typePrice = FLOORING_TYPE_PRICES[flooringType] || FLOORING_PRICE_PER_SQFT;
          
          totalLow += Math.round(flooringSqft * typePrice.low);
          totalHigh += Math.round(flooringSqft * typePrice.high);
          
          // Add removal cost if selected (radio button now - value is 'yes' or 'no')
          if (answers.flooring_removal === 'yes') {
            totalLow += Math.round(flooringSqft * FLOORING_REMOVAL_PRICE.low);
            totalHigh += Math.round(flooringSqft * FLOORING_REMOVAL_PRICE.high);
          }
          
          hasValidPrice = true;
        }
      }
    }
    
    // Legacy flooring sqft support (backward compatibility)
    if (!hasValidPrice && answers.flooring_sqft) {
      const sqft = parseFloat(answers.flooring_sqft) || 0;
      if (sqft > 0) {
        totalLow += Math.round(sqft * FLOORING_PRICE_PER_SQFT.low);
        totalHigh += Math.round(sqft * FLOORING_PRICE_PER_SQFT.high);
        hasValidPrice = true;
      }
    }
    
    // ==================== COUNTERTOPS: Square Footage Pricing ====================
    if (answers.countertops_sqft) {
      const sqft = parseFloat(answers.countertops_sqft) || 0;
      if (sqft > 0) {
        totalLow += Math.round(sqft * COUNTERTOPS_PRICE_PER_SQFT.low);
        totalHigh += Math.round(sqft * COUNTERTOPS_PRICE_PER_SQFT.high);
        hasValidPrice = true;
      }
    }
    
    // ==================== LANDSCAPING: No pricing yet - store values only ====================
    // Landscaping values (lot_size, house_sqft) are stored for future pricing logic
    
    // ==================== REMODEL/RESTORATION: Base estimate ====================
    if (answers.remodel_type && Array.isArray(answers.remodel_type) && answers.remodel_type.length > 0) {
      // Provide base range for remodel projects
      const baseRange = CATEGORY_BASE_PRICES["remodel-restoration"] || { low: 500, high: 25000 };
      totalLow += baseRange.low;
      totalHigh += baseRange.high;
      hasValidPrice = true;
    }
    
    // If no valid pricing answers yet, return null
    if (!hasValidPrice) return null;
    
    return { low: totalLow, high: totalHigh };
  }, [answers, homeSqft]);

  // Get questions for current category
  const getQuestions = () => {
    if (!selectedCategory) return [];
    const slug = selectedCategory.slug || "generic";
    return CATEGORY_QUESTIONS[slug] || GENERIC_QUESTIONS;
  };

  // Single visibility rule shared by getVisibleQuestions and handleAnswer
  const isQuestionVisible = (q, ans) => {
    // Check showIf condition (exact match)
    if (q.showIf) {
      for (const [key, value] of Object.entries(q.showIf)) {
        const answer = ans[key];
        if (Array.isArray(value)) {
          if (!value.includes(answer)) return false;
        } else {
          if (answer !== value) return false;
        }
      }
    }
    
    // Check showIfAnyService condition (for electrical follow-up questions)
    if (q.showIfAnyService) {
      const services = ans.electrical_services || [];
      if (!q.showIfAnyService.some(svc => services.includes(svc))) return false;
    }
    
    // Check showIfIncludes condition (array includes check)
    if (q.showIfIncludes) {
      for (const [key, value] of Object.entries(q.showIfIncludes)) {
        const answer = ans[key];
        if (!Array.isArray(answer) || !answer.includes(value)) return false;
      }
    }
    
    // Check showIfNot condition (hide if any value is included in answer)
    if (q.showIfNot) {
      for (const [key, values] of Object.entries(q.showIfNot)) {
        const answer = ans[key];
        if (Array.isArray(answer)) {
          // If answer is array, check if ANY of the values are included
          if (values.some(v => answer.includes(v))) return false;
        } else {
          // If answer is single value, check if it matches any in values
          if (values.includes(answer)) return false;
        }
      }
    }
    
    return true;
  };

  // Filter questions based on conditional logic
  const getVisibleQuestions = () => {
    return getQuestions().filter(q => isQuestionVisible(q, answers));
  };

  const visibleQuestions = getVisibleQuestions();
  const currentQuestion = visibleQuestions[currentQuestionIndex];
  const totalSteps = visibleQuestions.length + 2;
  const progress = ((step) / totalSteps) * 100;

  const handleCategorySelect = (cat) => {
    setSelectedCategory(cat);
    // Clear additional details when category changes (to avoid persisting text from previous category)
    setAdditionalDetails("");
    // Auto-advance to next step after selection
    setTimeout(() => {
      setStep(1);
    }, 300);
  };

  const handleCategoryContinue = () => {
    if (!selectedCategory) {
      toast.error("Please select a service");
      return;
    }
    setStep(1);
  };

  const [outOfArea, setOutOfArea] = useState(false);
  const [zipChecking, setZipChecking] = useState(false);

  const handleZipSubmit = async () => {
    if (zipCode.length < 5) {
      toast.error("Please enter a valid zip code");
      return;
    }
    setZipChecking(true);
    try {
      const res = await axios.get(`${API}/service-areas/validate-zip`, { params: { zip: zipCode } });
      if (res.data?.supported) {
        setOutOfArea(false);
        setStep(2);
      } else {
        setOutOfArea(true);
      }
    } catch (err) {
      // Fail-open: if the check fails, allow the customer through so we
      // don't block the entire wizard on a transient network hiccup.
      toast.error("Couldn't verify your ZIP right now — continuing.");
      setStep(2);
    } finally {
      setZipChecking(false);
    }
  };

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [currentQuestion.id]: value };
    setAnswers(newAnswers);
    
    setTimeout(() => {
      // Use the same visibility rules as the render path so indexes stay in sync
      const newVisible = getQuestions().filter(q => isQuestionVisible(q, newAnswers));
      
      const nextIndex = currentQuestionIndex + 1;
      if (nextIndex < newVisible.length) {
        setCurrentQuestionIndex(nextIndex);
        setStep(step + 1);
      } else {
        // Pass newAnswers to handle the case where timeline = quote_only
        navigateToContact(newAnswers);
      }
    }, 300);
  };

  const handleCheckboxAnswer = (optionValue, checked) => {
    const current = answers[currentQuestion.id] || [];
    let newValue;
    
    // Define "all" options and their corresponding individual options
    const allOptionsMap = {
      'which_bedrooms': { all: 'all_bedrooms', individuals: ['master_bedroom', 'second_bedroom', 'third_bedroom', 'more_bedrooms'] },
      'which_bathrooms': { all: 'all_bathrooms', individuals: ['master_bathroom', 'second_bathroom', 'third_bathroom'] },
      'popcorn_areas': { all: 'entire_house', individuals: ['living_room', 'bedrooms', 'kitchen', 'bathrooms'] }
    };
    
    const questionConfig = allOptionsMap[currentQuestion.id];
    
    if (checked) {
      if (questionConfig) {
        if (optionValue === questionConfig.all) {
          // Clicking "All" - remove all individual selections
          newValue = [optionValue];
        } else if (questionConfig.individuals.includes(optionValue)) {
          // Clicking an individual option - remove "All" if it was selected
          newValue = current.filter(v => v !== questionConfig.all);
          newValue = [...newValue, optionValue];
        } else {
          newValue = [...current, optionValue];
        }
      } else {
        newValue = [...current, optionValue];
      }
    } else {
      newValue = current.filter(v => v !== optionValue);
    }
    
    setAnswers({ ...answers, [currentQuestion.id]: newValue });
  };

  const handleCheckboxNext = () => {
    const current = answers[currentQuestion.id] || [];
    if (current.length === 0) {
      toast.error("Please select at least one option");
      return;
    }
    
    const newVisible = getVisibleQuestions();
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex < newVisible.length) {
      setCurrentQuestionIndex(nextIndex);
      setStep(step + 1);
    } else {
      navigateToContact();
    }
  };

  const handleTextNext = () => {
    const textValue = typeof answers[currentQuestion.id] === 'string' 
      ? answers[currentQuestion.id]?.trim() 
      : answers[currentQuestion.id]?.value;
    if (!textValue) {
      toast.error("Please enter a value");
      return;
    }
    
    const newVisible = getVisibleQuestions();
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex < newVisible.length) {
      setCurrentQuestionIndex(nextIndex);
      setStep(step + 1);
    } else {
      handleWizardComplete();
    }
  };

  // Route to the Photo Upload step before summary/contact
  const goToPhotoStep = (finalAnswers, priceEstimate) => {
    // Always bundle the unlisted-service description if the customer used the
    // "Can't find what you need?" prompt so it flows through to the backend
    // regardless of which flow (modular or legacy) triggers this navigation.
    const answersWithUnlisted = unlistedServiceDescription.trim()
      ? { ...finalAnswers, unlisted_service_description: unlistedServiceDescription.trim() }
      : finalAnswers;
    setPhotoStepData({
      finalAnswers: answersWithUnlisted,
      priceEstimate,
      isQuoteOnly: answersWithUnlisted.timeline === 'quote_only'
    });
    window.scrollTo(0, 0);
  };

  // After photos are added (or skipped), continue to quote summary or contact page
  const finishAfterPhotos = () => {
    if (!photoStepData) return;
    const { finalAnswers, priceEstimate, isQuoteOnly } = photoStepData;
    const uploaded = photoUpload.uploadedPhotos.map(p => ({
      photo_id: p.photo_id,
      caption: p.caption || "",
      original_filename: p.name
    }));
    const photoFields = {
      photo_session_id: uploaded.length > 0 ? photoUpload.sessionId : null,
      photos: uploaded
    };
    
    if (isQuoteOnly) {
      // Route to quote summary page
      navigate("/quote-summary", { state: { quoteData: {
        category: selectedCategory?.slug || categorySlug,
        categoryName: selectedCategory?.name || categorySlug,
        category_id: selectedCategory?.category_id,
        category_name: selectedCategory?.name,
        zipCode: zipCode,
        home_sqft: homeSqft || null,
        answers: finalAnswers,
        estimatedPrice: priceEstimate,
        lineItems: [],
        ...photoFields
      } } });
    } else {
      // Normal flow - go to contact page
      sessionStorage.setItem("wizardData", JSON.stringify({
        category: { id: selectedCategory?.category_id, name: selectedCategory?.name, slug: selectedCategory?.slug },
        category_id: selectedCategory?.category_id,
        category_name: selectedCategory?.name,
        zip_code: zipCode,
        home_sqft: homeSqft || null,
        answers: finalAnswers,
        estimated_range: priceEstimate,
        ...photoFields
      }));
      navigate("/request/contact");
    }
  };

  // Handle wizard completion - route based on timeline selection
  const handleWizardComplete = (latestAnswers = null) => {
    // Use latestAnswers if provided (for timing issues with radio buttons)
    const finalAnswers = latestAnswers || answers;
    
    // Add additional details AND unlisted-service description to the answers
    // (both fields are preserved separately — one never overwrites the other)
    const answersWithDetails = {
      ...finalAnswers,
      ...(additionalDetails.trim() && { additional_details: additionalDetails.trim() }),
      ...(unlistedServiceDescription.trim() && { unlisted_service_description: unlistedServiceDescription.trim() })
    };
    
    goToPhotoStep(answersWithDetails, estimatedPriceRange);
  };

  const navigateToContact = (latestAnswers = null) => {
    handleWizardComplete(latestAnswers);
  };

  const handleBack = () => {
    if (photoStepData) {
      setPhotoStepData(null);
      return;
    }
    if (step === 2 && currentQuestionIndex === 0) {
      setStep(1);
    } else if (step === 1) {
      if (!categorySlug) {
        setStep(0);
      } else {
        navigate('/request');
      }
    } else if (step === 0) {
      navigate('/');
    } else if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setStep(step - 1);
    }
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="request-wizard">
      {/* Header */}
      <header className="border-b border-gray-100 sticky top-0 bg-white z-50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-[#1F2933] hidden sm:inline">Contractor Hub LLC</span>
          </Link>
          
          <Button 
            variant="ghost" 
            onClick={handleBack}
            className="text-[#1F2933]/70"
            data-testid="wizard-header-back"
          >
            <ArrowLeft className="w-5 h-5 mr-2" /> Back
          </Button>
        </div>
        
        {/* Progress bar */}
        {step > 0 && (
          <div className="max-w-2xl mx-auto px-4 pb-4">
            <Progress value={progress} className="h-2" />
          </div>
        )}
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        {/* Step 0: Category Selection - Single Select */}
        {!photoStepData && step === 0 && (
          <div className="animate-fade-in">
            <h1 className="font-manrope font-bold text-2xl sm:text-3xl text-[#1F2933] text-center mb-2">
              What service do you need?
            </h1>
            <p className="text-[#1F2933]/60 text-center mb-8">
              Choose a service category
            </p>
            
            <div className="space-y-3">
              {categories.map(cat => {
                const isSelected = selectedCategory?.category_id === cat.category_id;
                return (
                  <button
                    key={cat.category_id}
                    onClick={() => handleCategorySelect(cat)}
                    className={`w-full p-4 border rounded-xl text-left transition-all flex items-center gap-4 ${
                      isSelected 
                        ? 'bg-[#0FA3B1]/5 border-[#0FA3B1]' 
                        : 'bg-gray-50 border-gray-200 hover:border-[#0FA3B1]/50'
                    }`}
                    data-testid={`category-${cat.slug}`}
                  >
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                      isSelected ? 'bg-[#0FA3B1] border-[#0FA3B1]' : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="w-4 h-4 text-white" />}
                    </div>
                    <img 
                      src={cat.image_url} 
                      alt={cat.name}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <span className="font-medium text-[#1F2933]">{cat.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 1: Zip Code (and Square Footage for Painting) — or Out-of-Area waitlist */}
        {!photoStepData && step === 1 && outOfArea && (
          <OutOfAreaWaitlist
            zipCode={zipCode}
            onChangeZip={() => { setOutOfArea(false); setZipCode(""); }}
          />
        )}

        {!photoStepData && step === 1 && !outOfArea && (
          <div className="animate-fade-in">
            <h1 className="font-manrope font-bold text-2xl sm:text-3xl text-[#1F2933] text-center mb-2">
              Tell us about your home
            </h1>
            <p className="text-[#1F2933]/60 text-center mb-8">
              We&apos;ll connect you with local pros
            </p>
            
            <div className="max-w-sm mx-auto space-y-6">
              {/* Square Footage Dropdown - For Painting and Plumbing */}
              {(selectedCategory?.slug === 'painting' || selectedCategory?.slug === 'plumbing') && (
                <div>
                  <label className="block text-sm font-medium text-[#1F2933] mb-2">
                    Home square footage
                  </label>
                  <Select value={homeSqft} onValueChange={setHomeSqft}>
                    <SelectTrigger className="w-full py-6 text-lg" data-testid="sqft-select">
                      <SelectValue placeholder="Select square footage" />
                    </SelectTrigger>
                    <SelectContent>
                      {SQFT_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value} data-testid={`sqft-${option.value}`}>
                          {sanitizeDisplayText(option.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Zip Code */}
              <div>
                <label className="block text-sm font-medium text-[#1F2933] mb-2">
                  Zip code
                </label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1F2933]/40" />
                  <Input
                    type="text"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="Enter zip code"
                    className="pl-12 py-6 text-lg"
                    maxLength={5}
                    data-testid="zip-input"
                  />
                </div>
              </div>
              
              <Button 
                onClick={handleZipSubmit}
                className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl"
                disabled={zipChecking || zipCode.length < 5 || ((selectedCategory?.slug === 'painting' || selectedCategory?.slug === 'plumbing') && !homeSqft)}
                data-testid="zip-submit"
              >
                {zipChecking ? "Checking your area…" : "Continue"} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2+: Questions */}
        {/* Plumbing uses modular PlumbingFlow component */}
        {!photoStepData && step >= 2 && selectedCategory?.slug === 'plumbing' && (
          <div className="animate-fade-in">
            <PlumbingFlow
              answers={answers}
              setAnswers={setAnswers}
              additionalDetails={additionalDetails}
              setAdditionalDetails={setAdditionalDetails}
              unlistedServiceDescription={unlistedServiceDescription}
              setUnlistedServiceDescription={setUnlistedServiceDescription}
              onComplete={(finalAnswers, priceEstimate) => {
                // Go to the Photo Upload step before review/contact
                goToPhotoStep(finalAnswers, priceEstimate);
              }}
              onBack={() => setStep(1)}
            />
          </div>
        )}

        {/* Handyman uses modular HandymanFlow component */}
        {!photoStepData && step >= 2 && selectedCategory?.slug === 'handyman' && (
          <div className="animate-fade-in">
            <HandymanFlow
              answers={answers}
              setAnswers={setAnswers}
              additionalDetails={additionalDetails}
              setAdditionalDetails={setAdditionalDetails}
              unlistedServiceDescription={unlistedServiceDescription}
              setUnlistedServiceDescription={setUnlistedServiceDescription}
              onComplete={() => {
                // Calculate estimate using modular pricing, then go to Photo Upload step
                const priceEstimate = calculateHandymanEstimate(answers);
                goToPhotoStep({ ...answers, additional_details: additionalDetails }, priceEstimate);
              }}
              onBack={() => setStep(1)}
            />
          </div>
        )}

        {/* Painting uses modular PaintingFlow component */}
        {!photoStepData && step >= 2 && selectedCategory?.slug === 'painting' && (
          <div className="animate-fade-in">
            <PaintingFlow
              answers={answers}
              setAnswers={setAnswers}
              additionalDetails={additionalDetails}
              setAdditionalDetails={setAdditionalDetails}
              unlistedServiceDescription={unlistedServiceDescription}
              setUnlistedServiceDescription={setUnlistedServiceDescription}
              homeSqftValue={SQFT_OPTIONS.find(opt => opt.value === homeSqft)?.sqft || 1500}
              onComplete={(finalAnswers, priceEstimate) => {
                // Go to the Photo Upload step before review/contact
                goToPhotoStep(finalAnswers, priceEstimate);
              }}
              onBack={() => setStep(1)}
            />
          </div>
        )}

        {/* Electrical uses modular ElectricalFlow component */}
        {!photoStepData && step >= 2 && selectedCategory?.slug === 'electrical' && (
          <div className="animate-fade-in">
            <ElectricalFlow
              answers={answers}
              setAnswers={setAnswers}
              additionalDetails={additionalDetails}
              setAdditionalDetails={setAdditionalDetails}
              unlistedServiceDescription={unlistedServiceDescription}
              setUnlistedServiceDescription={setUnlistedServiceDescription}
              onComplete={(finalAnswers, priceEstimate) => {
                goToPhotoStep(finalAnswers, priceEstimate);
              }}
              onBack={() => setStep(1)}
            />
          </div>
        )}

        {/* Step 2+: Questions (for all other categories - legacy flow) */}
        {!photoStepData && step >= 2 && selectedCategory?.slug !== 'plumbing' && selectedCategory?.slug !== 'handyman' && selectedCategory?.slug !== 'painting' && selectedCategory?.slug !== 'electrical' && currentQuestion && (
          <div className="animate-fade-in">
            <h1 className="font-manrope font-bold text-2xl sm:text-3xl text-[#1F2933] text-center mb-4">
              {sanitizeDisplayText(currentQuestion.question)}
            </h1>
            <p className="text-[#1F2933]/60 text-sm text-center mb-8">
              {PRICE_TRUST_LINE}
            </p>
            
            {currentQuestion.type === "radio" && (
              <div className="bg-gray-50 rounded-2xl overflow-hidden">
                <RadioGroup
                  value={answers[currentQuestion.id] || ""}
                  onValueChange={handleAnswer}
                >
                  {currentQuestion.options.map((option, idx) => (
                    <label
                      key={option.value}
                      onClick={() => {
                        // Re-clicking the already-selected option advances again
                        // (RadioGroup onValueChange only fires on change)
                        if (answers[currentQuestion.id] === option.value) {
                          handleAnswer(option.value);
                        }
                      }}
                      className={`flex items-center gap-4 p-5 cursor-pointer border-b border-gray-200 last:border-b-0 hover:bg-white transition-colors ${
                        answers[currentQuestion.id] === option.value ? 'bg-white' : ''
                      }`}
                      data-testid={`option-${option.value}`}
                    >
                      <RadioGroupItem 
                        value={option.value} 
                        className="border-2 border-gray-300 text-[#0FA3B1] data-[state=checked]:border-[#0FA3B1]"
                      />
                      <span className="text-[#1F2933] font-medium">{sanitizeDisplayText(option.label)}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            )}

            {currentQuestion.type === "checkbox" && (
              <>
                <div className="bg-gray-50 rounded-2xl overflow-hidden mb-6">
                  {currentQuestion.options.map((option, idx) => {
                    const isChecked = (answers[currentQuestion.id] || []).includes(option.value);
                    return (
                      <label
                        key={option.value}
                        className={`flex items-center gap-4 p-5 cursor-pointer border-b border-gray-200 last:border-b-0 hover:bg-white transition-colors ${
                          isChecked ? 'bg-white' : ''
                        }`}
                        data-testid={`option-${option.value}`}
                      >
                        <Checkbox 
                          checked={isChecked}
                          onCheckedChange={(checked) => handleCheckboxAnswer(option.value, checked)}
                          className="border-2 border-gray-300 data-[state=checked]:bg-[#0FA3B1] data-[state=checked]:border-[#0FA3B1]"
                        />
                        <span className="text-[#1F2933] font-medium">{sanitizeDisplayText(option.label)}</span>
                      </label>
                    );
                  })}
                </div>
                
                <Button 
                  onClick={handleCheckboxNext}
                  className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl"
                  data-testid="checkbox-next"
                >
                  Continue <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </>
            )}

            {/* Collapsible Categories - Used for Handyman services */}
            {currentQuestion.type === "collapsible_categories" && (
              <>
                {currentQuestion.subtitle && (
                  <p className="text-sm text-[#1F2933]/60 mb-4">{currentQuestion.subtitle}</p>
                )}
                
                <div className="space-y-3 mb-6">
                  {currentQuestion.categories.map((category) => {
                    const isExpanded = expandedCategories.includes(category.id);
                    const selectedServices = answers[currentQuestion.id] || [];
                    const selectedInCategory = category.services.filter(s => 
                      selectedServices.includes(s.value)
                    ).length;
                    
                    return (
                      <div 
                        key={category.id} 
                        className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200"
                        data-testid={`category-group-${category.id}`}
                      >
                        {/* Category Header - Clickable to expand/collapse */}
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedCategories(prev => 
                              prev.includes(category.id)
                                ? prev.filter(id => id !== category.id)
                                : [...prev, category.id]
                            );
                          }}
                          className="w-full flex items-center justify-between p-4 hover:bg-gray-100 transition-colors"
                          data-testid={`category-toggle-${category.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xl">{category.emoji}</span>
                            <span className="font-semibold text-[#1F2933]">{category.name}</span>
                            {selectedInCategory > 0 && (
                              <span className="bg-[#0FA3B1] text-white text-xs px-2 py-0.5 rounded-full">
                                {selectedInCategory} selected
                              </span>
                            )}
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-[#1F2933]/50" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-[#1F2933]/50" />
                          )}
                        </button>
                        
                        {/* Category Services - Shown when expanded */}
                        {isExpanded && (
                          <div className="border-t border-gray-200 bg-white">
                            {category.services.map((service) => {
                              const isChecked = selectedServices.includes(service.value);
                              return (
                                <label
                                  key={service.value}
                                  className={`flex items-center gap-4 p-4 pl-12 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors ${
                                    isChecked ? 'bg-[#0FA3B1]/5' : ''
                                  }`}
                                  data-testid={`service-${service.value}`}
                                >
                                  <Checkbox 
                                    checked={isChecked}
                                    onCheckedChange={(checked) => {
                                      const current = answers[currentQuestion.id] || [];
                                      if (checked) {
                                        setAnswers({ 
                                          ...answers, 
                                          [currentQuestion.id]: [...current, service.value] 
                                        });
                                      } else {
                                        setAnswers({ 
                                          ...answers, 
                                          [currentQuestion.id]: current.filter(v => v !== service.value) 
                                        });
                                      }
                                    }}
                                    className="border-2 border-gray-300 data-[state=checked]:bg-[#0FA3B1] data-[state=checked]:border-[#0FA3B1]"
                                  />
                                  <div className="flex-1">
                                    <span className="text-[#1F2933]">{service.label}</span>
                                    {service.price && (
                                      <span className="text-xs text-[#1F2933]/50 ml-2">
                                        ${service.price.low}–${service.price.high}
                                      </span>
                                    )}
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Selected count and Continue button */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-[#1F2933]/60">
                    {(answers[currentQuestion.id] || []).length} service(s) selected
                  </span>
                  {(answers[currentQuestion.id] || []).length > 0 && (
                    <button
                      type="button"
                      onClick={() => setAnswers({ ...answers, [currentQuestion.id]: [] })}
                      className="text-sm text-red-500 hover:text-red-600"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                
                <Button 
                  onClick={handleCheckboxNext}
                  disabled={(answers[currentQuestion.id] || []).length === 0}
                  className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl disabled:opacity-50"
                  data-testid="collapsible-next"
                >
                  Continue <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </>
            )}

            {currentQuestion.type === "text" && (
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-2xl p-6">
                  <Input
                    type="text"
                    placeholder={currentQuestion.placeholder || "Enter your answer..."}
                    value={answers[currentQuestion.id] || ""}
                    onChange={(e) => setAnswers({ ...answers, [currentQuestion.id]: e.target.value })}
                    className="bg-white border-gray-200 text-[#1F2933] text-lg p-4 h-auto"
                    data-testid="text-input"
                  />
                </div>
                
                <Button 
                  onClick={handleTextNext}
                  disabled={!answers[currentQuestion.id]?.trim()}
                  className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl disabled:opacity-50"
                  data-testid="text-next"
                >
                  Continue <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {/* Number Input (for square footage) */}
            {currentQuestion.type === "number" && (
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-2xl p-6">
                  <div className="relative">
                    <Input
                      type="number"
                      placeholder={currentQuestion.placeholder || "Enter number"}
                      value={answers[currentQuestion.id] || ""}
                      onChange={(e) => setAnswers({ ...answers, [currentQuestion.id]: e.target.value })}
                      className="bg-white border-gray-200 text-[#1F2933] text-lg p-4 h-auto pr-20"
                      data-testid="number-input"
                    />
                    {currentQuestion.unit && (
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1F2933]/50">
                        {currentQuestion.unit}
                      </span>
                    )}
                  </div>
                </div>
                
                <Button 
                  onClick={handleTextNext}
                  disabled={!answers[currentQuestion.id]}
                  className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl disabled:opacity-50"
                  data-testid="number-next"
                >
                  Continue <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {/* Number with Unit selector (for lot size) */}
            {currentQuestion.type === "number_with_unit" && (
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-2xl p-6">
                  <div className="flex gap-3">
                    <div className="flex-1 relative">
                      <Input
                        type="number"
                        placeholder={currentQuestion.placeholder || "Enter size"}
                        value={answers[currentQuestion.id]?.value || ""}
                        onChange={(e) => setAnswers({ 
                          ...answers, 
                          [currentQuestion.id]: { 
                            ...answers[currentQuestion.id],
                            value: e.target.value 
                          } 
                        })}
                        className="bg-white border-gray-200 text-[#1F2933] text-lg p-4 h-auto"
                        data-testid="number-unit-input"
                      />
                    </div>
                    <Select
                      value={answers[currentQuestion.id]?.unit || currentQuestion.units?.[0]?.value || ""}
                      onValueChange={(value) => setAnswers({ 
                        ...answers, 
                        [currentQuestion.id]: { 
                          ...answers[currentQuestion.id],
                          unit: value 
                        } 
                      })}
                    >
                      <SelectTrigger className="w-40 bg-white border-gray-200 text-[#1F2933] h-auto py-4" data-testid="unit-select">
                        <SelectValue placeholder="Unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {currentQuestion.units?.map(unit => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <Button 
                  onClick={handleTextNext}
                  disabled={!answers[currentQuestion.id]?.value}
                  className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl disabled:opacity-50"
                  data-testid="number-unit-next"
                >
                  Continue <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {/* Additional Details (Optional) - shown on all question steps */}
            <div className="mt-6">
              <label 
                htmlFor="additional-details"
                className="block text-sm font-medium text-[#1F2933]/80 mb-2"
              >
                Additional Details (Optional)
              </label>
              <Textarea
                id="additional-details"
                value={additionalDetails}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= MAX_ADDITIONAL_DETAILS) {
                    setAdditionalDetails(value);
                  }
                }}
                placeholder="Share any specific requirements, measurements, preferences, or unique project details that aren't covered above..."
                className="bg-gray-50 border-gray-200 text-[#1F2933] resize-none min-h-[100px]"
                maxLength={MAX_ADDITIONAL_DETAILS}
                data-testid="additional-details-textarea"
              />
              <div className="flex justify-end mt-1">
                <span className={`text-xs ${additionalDetails.length >= MAX_ADDITIONAL_DETAILS ? 'text-red-500' : 'text-[#1F2933]/50'}`}>
                  {additionalDetails.length}/{MAX_ADDITIONAL_DETAILS}
                </span>
              </div>
            </div>

            {/* "Can't find what you need?" — legacy flow: show on first question only */}
            {currentQuestionIndex === 0 && (
              <CustomServiceRequest
                value={unlistedServiceDescription}
                onChange={setUnlistedServiceDescription}
              />
            )}

            {/* Estimated Labor Cost - always show, with placeholder until priced */}
            <EstimateCard priceRange={estimatedPriceRange} isCustomRequest={!estimatedPriceRange && !!unlistedServiceDescription.trim()} />
          </div>
        )}

        {/* Photo Upload Step - after service questions, before review/contact */}
        {photoStepData && (
          <PhotoUpload
            photoUpload={photoUpload}
            onContinue={finishAfterPhotos}
            onSkip={finishAfterPhotos}
          />
        )}
      </main>
    </div>
  );
}
